import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

const inputCls =
  "w-full bg-sand rounded-none border-b-2 border-transparent focus:border-ink outline-none px-4 py-3.5 text-sm placeholder:text-smoke/60 transition-colors duration-300";

export const Contact = ({ t, lang }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", location: "", service: "", website: "", message: "", privacy: false });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang, page: window.location.pathname, referrer: document.referrer || null });
      toast.success(t.contact.form.success);
      setForm({ name: "", phone: "", email: "", location: "", service: "", website: "", message: "", privacy: false });
    } catch {
      toast.error(t.contact.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="border-t border-line" data-testid="contact-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2">
        <div className="px-6 md:px-10 py-24 md:py-32 lg:border-r border-line">
          <motion.p {...fadeUp} className="text-xs uppercase tracking-[0.25em] font-bold text-taupe mb-6" data-testid="contact-overline">
            {t.contact.overline}
          </motion.p>
          <motion.h2
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="font-heading font-light tracking-tight text-4xl md:text-6xl leading-[1.02]"
            data-testid="contact-title"
          >
            {t.contact.title}
          </motion.h2>

          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-14 space-y-9"
            data-testid="contact-details"
          >
            <a
              href="mailto:info@swiss-pa-services.ch"
              data-testid="contact-email-link"
              className="group flex items-start gap-5"
            >
              <Mail size={20} strokeWidth={1.25} className="mt-1 text-taupe" />
              <span>
                <span className="block text-xs uppercase tracking-[0.2em] text-smoke font-semibold">{t.contact.emailLabel}</span>
                <span className="mt-1.5 block font-heading text-2xl md:text-3xl group-hover:text-taupe transition-colors duration-300">
                  info@swiss-pa-services.ch
                </span>
              </span>
            </a>
            <a href="tel:+41446880380" data-testid="contact-phone-link" className="group flex items-start gap-5">
              <Phone size={20} strokeWidth={1.25} className="mt-1 text-taupe" />
              <span>
                <span className="block text-xs uppercase tracking-[0.2em] text-smoke font-semibold">{t.contact.phoneLabel}</span>
                <span className="mt-1.5 block font-heading text-2xl md:text-3xl group-hover:text-taupe transition-colors duration-300">
                  +41 44 688 03 80
                </span>
              </span>
            </a>
            <div className="flex items-start gap-5" data-testid="contact-address">
              <MapPin size={20} strokeWidth={1.25} className="mt-1 text-taupe" />
              <span>
                <span className="block text-xs uppercase tracking-[0.2em] text-smoke font-semibold">{t.contact.addressLabel}</span>
                <span className="mt-1.5 block font-heading text-2xl md:text-3xl">
                  Baarerstrasse 43, 6300 Zug
                </span>
              </span>
            </div>
          </motion.div>
        </div>

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.15 }}
          className="px-6 md:px-10 py-24 md:py-32 bg-sand/50"
        >
          <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
            <div>
              <label htmlFor="contact-name" className="block text-xs uppercase tracking-[0.2em] font-semibold text-smoke mb-2">
                {t.contact.form.name} *
              </label>
              <input
                id="contact-name"
                data-testid="contact-form-name-input"
                required
                minLength={2}
                value={form.name}
                onChange={set("name")}
                className={inputCls}
                placeholder={t.contact.form.name}
              />
            </div>
            <div>
              <label htmlFor="contact-phone" className="block text-xs uppercase tracking-[0.2em] font-semibold text-smoke mb-2">
                {t.contact.form.phone} *
              </label>
              <input
                id="contact-phone"
                type="tel"
                data-testid="contact-form-phone-input"
                required
                minLength={6}
                value={form.phone}
                onChange={set("phone")}
                className={inputCls}
                placeholder={t.contact.form.phone}
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-xs uppercase tracking-[0.2em] font-semibold text-smoke mb-2">
                {t.contact.form.email} *
              </label>
              <input
                id="contact-email"
                type="email"
                data-testid="contact-form-email-input"
                required
                value={form.email}
                onChange={set("email")}
                className={inputCls}
                placeholder={t.contact.form.email}
              />
            </div>
            <div>
              <label htmlFor="contact-location" className="block text-xs uppercase tracking-[0.2em] font-semibold text-smoke mb-2">
                {t.contact.form.location} *
              </label>
              <input
                id="contact-location"
                data-testid="contact-form-location-input"
                required
                minLength={2}
                value={form.location}
                onChange={set("location")}
                className={inputCls}
                placeholder={t.contact.form.location}
              />
            </div>
            <div>
              <label htmlFor="contact-service" className="block text-xs uppercase tracking-[0.2em] font-semibold text-smoke mb-2">
                {t.contact.form.service} *
              </label>
              <select
                id="contact-service"
                data-testid="contact-form-service-select"
                required
                value={form.service}
                onChange={set("service")}
                className={`${inputCls} cursor-pointer`}
              >
                <option value="" disabled>{t.contact.form.servicePlaceholder}</option>
                {t.contact.form.serviceOptions.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="hidden" aria-hidden="true">
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={set("website")}
              />
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-xs uppercase tracking-[0.2em] font-semibold text-smoke mb-2">
                {t.contact.form.message} *
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-form-message-input"
                required
                minLength={5}
                rows={5}
                value={form.message}
                onChange={set("message")}
                className={`${inputCls} resize-none`}
                placeholder={t.contact.form.message}
              />
            </div>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="contact-form-privacy-label">
              <input
                type="checkbox"
                required
                checked={form.privacy}
                onChange={set("privacy")}
                data-testid="contact-form-privacy-checkbox"
                className="mt-1 h-4 w-4 accent-[#1A1A1A]"
              />
              <span className="text-xs text-smoke leading-relaxed">{t.contact.form.privacy}</span>
            </label>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-form-submit-button"
              className="inline-flex items-center gap-2 bg-ink text-cream px-8 py-4 text-sm font-semibold tracking-wide hover:bg-taupe disabled:opacity-60 transition-colors duration-300"
            >
              {sending ? t.contact.form.sending : t.contact.form.submit}
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
